import type { HuntLiveDto, HuntPoll } from '@/hooks/use-automation'

/**
 * Everything the hunt panel displays, derived in ONE pass over the ring.
 *
 * Pure, no React, no formatting of anything that a memoised row could format
 * itself. It exists as its own module for two reasons: the panel is four tabs
 * that would otherwise each re-derive the same numbers, and a derivation this
 * dense is far easier to reason about when it is not interleaved with JSX.
 *
 * ── The rule that matters most ──────────────────────────────────────
 * This may only COLLECT REFERENCES out of `recent`. It must never `.map()` the
 * ring into fresh objects. React Query's structural sharing hands back the very
 * same `HuntPoll` object for a record that has not changed, which is what lets
 * `React.memo` on a row bail on a single `Object.is`. One innocent
 * `recent.map(p => ({ ...p, pretty }))` would replace every reference each poll
 * and turn twenty free bails into twenty full re-renders, 1.4 times a second,
 * for hours. Per-row formatting belongs inside the row.
 */

/* ------------------------------------------------------------------ *
 * Vocabulary
 * ------------------------------------------------------------------ */

export type Tone = 'ok' | 'warn' | 'danger'

export interface Verdict {
  worker: number
  tone: Exclude<Tone, 'ok'>
  /** Written for a live hunt. `pastText` is the same finding, after the fact. */
  text: string
  pastText: string
}

/** Per-worker statistics over the visible window, not over the hunt's lifetime. */
export interface WorkerWindow {
  n: number
  okN: number
  refusals: number
  p50Ttfb: number
  p90Ttfb: number
  /** Distinct identities seen in the window, newest first. */
  accounts: string[]
  /** Longest run of consecutive checks on ONE identity. The stall signal. */
  longestRun: number
}

export interface Digest {
  /** References, newest first, holes dropped. Never copies. */
  newestFirst: HuntPoll[]
  refusals: HuntPoll[]
  /** Workers that have completed at least one check. Drives the stage bar. */
  reported: number
  okN: number
  p50: number
  p50Ttfb: number
  cold: number
  bytes: number
  enc: string
  per: (WorkerWindow | undefined)[]
  /** Shared upper bound so every worker's latency meter is comparable. */
  meterMax: number
  verdicts: Verdict[]
  health: Record<number, Tone>
  /** Set when every egress shares a host, so rows can show just the port. */
  egressPrefix: string
  windowStatusCounts: [number, number][]
}

/* ------------------------------------------------------------------ *
 * Thresholds
 * ------------------------------------------------------------------ */

/**
 * The server's own throttle test, mirrored exactly.
 *
 * `schedule-hunt.ts` rotates an address when its ttfb passes
 * `max(best × 2, best + 200)`. The panel draws the same line so that what the
 * operator sees flagged and what the rotator acts on are the same event — a
 * console with its own private threshold would confidently disagree with the
 * logs, and the operator would have no way to tell which was right.
 */
export function throttleThreshold(bestTtfbMs: number): number {
  return bestTtfbMs <= 0 ? 0 : Math.max(bestTtfbMs * 2, bestTtfbMs + 200)
}

/** A request that never produced a schedule. `0` is "no reply at all". */
export function failed(status: number): boolean {
  return status === 0 || status >= 400
}

/**
 * How long a silent worker is allowed to be silent.
 *
 * Scaled off the observed round trip rather than fixed: eight round trips is
 * "several checks late" on any network, while a constant would cry wolf on a
 * slow link and stay quiet for far too long on a fast one. Floored so a very
 * fast hunt does not flag ordinary jitter.
 */
function stallMs(meanRttMs: number): number {
  return Math.max(5_000, 8 * meanRttMs)
}

/* ------------------------------------------------------------------ *
 * The pass
 * ------------------------------------------------------------------ */

export function buildDigest(hunt: HuntLiveDto): Digest {
  const recent = hunt.recent
  const workers = hunt.workers

  const newestFirst: HuntPoll[] = []
  const refusals: HuntPoll[] = []
  const okMs: number[] = []
  const okTtfb: number[] = []
  const statusCounts = new Map<number, number>()
  const per: (WorkerWindow | undefined)[] = []
  const ttfbBy: number[][] = []
  /** Per worker: the account on the previous (newer) record, for run-length. */
  const prevAccount: (string | undefined)[] = []
  const runLen: number[] = []

  let cold = 0
  let bytes = 0
  let enc = ''

  // Backwards: newest first is the order every list renders in, so walking that
  // way means no reverse afterwards and the freshest values land first.
  for (let i = recent.length - 1; i >= 0; i -= 1) {
    const p = recent[i]
    // `!= null`, not `!== undefined`: a sparse array on the server serialises
    // its holes as JSON null, which reads as present and throws on access.
    if (p == null) continue

    newestFirst.push(p)
    if (newestFirst.length === 1) {
      bytes = p.bytes
      enc = p.enc
    }
    if (!p.warm) cold += 1

    const bad = failed(p.status)
    if (bad) {
      refusals.push(p)
      statusCounts.set(p.status, (statusCounts.get(p.status) ?? 0) + 1)
    } else {
      /*
       * Percentiles over SUCCESSFUL checks only.
       *
       * A 429 is turned away at the edge and comes back fast, so folding
       * refusals into the latency figures makes a hunt that is being
       * rate-limited report a FASTER number while getting worse. Same reason
       * `meanRttMs` is never displayed.
       */
      okMs.push(p.ms)
      okTtfb.push(p.ttfbMs)
    }

    const w = p.worker
    let win = per[w]
    if (win === undefined) {
      win = { n: 0, okN: 0, refusals: 0, p50Ttfb: 0, p90Ttfb: 0, accounts: [], longestRun: 0 }
      per[w] = win
      ttfbBy[w] = []
      runLen[w] = 0
    }
    win.n += 1
    if (bad) win.refusals += 1
    else {
      win.okN += 1
      ttfbBy[w]?.push(p.ttfbMs)
    }
    if (!win.accounts.includes(p.account)) win.accounts.push(p.account)

    // Run length over adjacent records for this worker, in walk order.
    if (prevAccount[w] === p.account) runLen[w] = (runLen[w] ?? 0) + 1
    else runLen[w] = 1
    prevAccount[w] = p.account
    if ((runLen[w] ?? 0) > win.longestRun) win.longestRun = runLen[w] ?? 0
  }

  for (let w = 0; w < per.length; w += 1) {
    const win = per[w]
    const xs = ttfbBy[w]
    if (win === undefined || xs === undefined || xs.length === 0) continue
    const sorted = [...xs].sort((a, b) => a - b)
    win.p50Ttfb = sorted[Math.floor(sorted.length / 2)] ?? 0
    win.p90Ttfb = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.9))] ?? 0
  }

  const reported = workers.reduce((n, w) => (w != null && w.polls > 0 ? n + 1 : n), 0)

  /*
   * One scale for every meter, so five bars can be compared by eye.
   *
   * Per-worker scaling would make a throttled address and a healthy one draw
   * identical bars — each full against its own maximum — which is precisely the
   * comparison the meter exists to support.
   */
  let meterMax = 200
  for (const w of workers) {
    if (w == null) continue
    const win = per[w.worker]
    meterMax = Math.max(meterMax, win?.p90Ttfb ?? 0, w.bestTtfbMs * 2)
  }

  const { verdicts, health } = judge(hunt, per, stallMs(hunt.meanRttMs))

  return {
    newestFirst,
    refusals,
    reported,
    okN: okMs.length,
    p50: median(okMs),
    p50Ttfb: median(okTtfb),
    cold,
    bytes,
    enc,
    per,
    meterMax,
    verdicts,
    health,
    egressPrefix: commonHost(workers),
    windowStatusCounts: [...statusCounts.entries()].sort((a, b) => b[1] - a[1]),
  }
}

function median(xs: number[]): number {
  if (xs.length === 0) return 0
  const s = [...xs].sort((a, b) => a - b)
  return s[Math.floor(s.length / 2)] ?? 0
}

/* ------------------------------------------------------------------ *
 * Findings
 * ------------------------------------------------------------------ */

/**
 * Turn the numbers into sentences, worst first.
 *
 * The panel's whole purpose is that nobody should have to read a table to learn
 * that something is wrong. Each rule states the finding AND its evidence, so a
 * verdict can be checked rather than merely trusted.
 *
 * At most two per worker: a worker that trips four rules is one broken worker,
 * and printing four lines about it would bury the other four workers.
 */
function judge(
  hunt: HuntLiveDto,
  per: (WorkerWindow | undefined)[],
  stall: number
): { verdicts: Verdict[]; health: Record<number, Tone> } {
  const verdicts: Verdict[] = []
  const health: Record<number, Tone> = {}
  const arming = hunt.live && hunt.workers.every((w) => w == null || w.polls === 0)
  /** Workers deliberately held for a session renewal — silence is expected. */
  const held = hunt.live && hunt.paused === true

  const worse = (a: Tone, b: Tone): Tone =>
    a === 'danger' || b === 'danger' ? 'danger' : a === 'warn' || b === 'warn' ? 'warn' : 'ok'

  for (const w of hunt.workers) {
    if (w == null) continue
    const win = per[w.worker]
    const mine: Verdict[] = []

    const push = (tone: 'warn' | 'danger', text: string, pastText: string) => {
      if (mine.length < 2) mine.push({ worker: w.worker, tone, text, pastText })
    }

    /*
     * Neither time-based verdict is meaningful while the hunt is HELD.
     *
     * Both are inferences from silence — "no check for 8s", "still has not
     * completed one" — and during a renewal the silence is deliberate and
     * shared by every worker. Reporting them would paint five danger rows on a
     * hunt that is working exactly as designed, at the one moment an operator
     * most needs to trust what the panel says. The structural findings below
     * (an empty ring, a throttled address) are still true and still shown.
     */
    if (held) {
      /* deliberately silent — see above */
    } else if (w.parked) {
      /*
       * Ahead of the time-based lines, and that ORDER is the whole point.
       *
       * A parked worker holds no address, so it never reaches the poll loop and
       * never completes a check — which means `lastAgoMs` climbs without bound
       * and the branch below fires, reporting a socket that has not returned.
       * There is no socket. That verdict sent an investigation after the network
       * for hours while the actual finding — the ISP pool had nothing to give —
       * was not stated anywhere in the panel, and the worker's egress column
       * still showed the address it used to hold.
       *
       * Naming the cause rather than the symptom is what this whole file is for.
       */
      push(
        'danger',
        'no address — the ISP pool had none free, so this worker is not watching',
        'had no address — the ISP pool had none free, so it was not watching'
      )
    } else if (w.lastAgoMs !== null && w.lastAgoMs > stall) {
      push(
        'danger',
        `no completed check for ${(w.lastAgoMs / 1000).toFixed(1)}s — its socket has not returned`,
        `had made no completed check for ${(w.lastAgoMs / 1000).toFixed(1)}s when the hunt ended`
      )
    } else if (w.polls === 0 && !arming) {
      // Silent while everyone else is arming is not a fault; silent while the
      // others are already reporting is the whole point of this line.
      push('warn', 'has still not completed a check', 'never completed a check')
    }

    if (w.accountsTotal > 0 && w.accounts === 0) {
      push(
        'danger',
        "ring empty — every check is going out on the customer's own session",
        "ring was empty — checks were going out on the customer's own session"
      )
    }

    const threshold = throttleThreshold(w.bestTtfbMs)
    if (threshold > 0 && win !== undefined && win.okN >= 3 && win.p50Ttfb >= threshold) {
      push(
        'warn',
        `throttled — ttfb ${win.p50Ttfb}ms against its own best of ${w.bestTtfbMs}ms`,
        `was throttled — ttfb ${win.p50Ttfb}ms against its own best of ${w.bestTtfbMs}ms`
      )
    }

    if (
      w.accountsTotal > 1 &&
      win !== undefined &&
      win.n >= 4 &&
      (win.accounts.length === 1 || win.longestRun >= 4)
    ) {
      push(
        'warn',
        `ring stalled — ${win.longestRun} checks in a row as ${win.accounts[0] ?? 'one identity'}`,
        `ring had stalled on ${win.accounts[0] ?? 'one identity'}`
      )
    }

    if (win !== undefined && win.n >= 8 && win.refusals / win.n >= 0.25) {
      push(
        'warn',
        `refused on ${win.refusals} of ${win.n} recent checks`,
        `was refused on ${win.refusals} of its last ${win.n} checks`
      )
    }

    for (const v of mine) {
      verdicts.push(v)
      health[w.worker] = worse(health[w.worker] ?? 'ok', v.tone)
    }
  }

  // Danger first, then by worker, so the ordering is stable across polls and a
  // line does not jump while it is being read.
  verdicts.sort((a, b) =>
    a.tone === b.tone ? a.worker - b.worker : a.tone === 'danger' ? -1 : 1
  )
  return { verdicts, health }
}

/* ------------------------------------------------------------------ *
 * Formatting shared by more than one tab
 * ------------------------------------------------------------------ */

/**
 * The host every worker shares, or '' when they differ.
 *
 * All five endpoints are normally the same Oxylabs host on different ports, so
 * repeating `isp.oxylabs.io` on every row spends the width the account name and
 * the timings need. Printed once at the foot of the strip instead.
 */
function commonHost(workers: HuntLiveDto['workers']): string {
  let host = ''
  for (const w of workers) {
    // A parked worker's label is a word, not a `host:port`, so it has no host to
    // agree with. Skipped rather than allowed to fall into the `i <= 0` bail
    // below, which would give up on shortening for the whole strip — one worker
    // without an address would then widen every other row's egress column.
    if (w == null || w.parked || w.egress === '—' || w.egress === '') continue
    const i = w.egress.lastIndexOf(':')
    if (i <= 0) return ''
    const h = w.egress.slice(0, i)
    if (host === '') host = h
    else if (host !== h) return ''
  }
  return host
}

export function shortEgress(label: string, prefix: string): string {
  if (prefix === '' || !label.startsWith(prefix)) return label
  return label.slice(prefix.length)
}

/** Milliseconds shown, because that is the unit this system races in. */
export function stamp(at: number): string {
  const d = new Date(at)
  const p = (n: number, w = 2) => String(n).padStart(w, '0')
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`
}

export function kb(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`
}

/**
 * An account name short enough to sit on a check row.
 *
 * The local part alone — the domain is identical for every account in the pool,
 * so printing it twenty times would push the timings off the line. The full
 * address goes on the row's `title`, and "own session" is left intact because
 * that distinction is the entire point of the column.
 */
export function shortAccount(account: string): string {
  if (account === '' || account === '—') return '—'
  const at = account.indexOf('@')
  return at === -1 ? account : account.slice(0, at)
}

/**
 * A pool row's `_id`, shortened from the MIDDLE.
 *
 * These ids are `al_` plus a timestamp plus a counter, so every account claimed
 * in the same run shares a long identical prefix and differs only in its last
 * few characters. Truncating with `text-overflow: ellipsis` — which clips the
 * END — therefore rendered five different accounts as five identical strings:
 * a column that looked like a constant, in a table whose entire purpose is
 * telling one identity from another.
 *
 * Head and tail, so the value is recognisable AND distinguishable. The full id
 * stays in the `title` and in the copy-as-text export, because that is the form
 * a Mongo query takes.
 */
export function shortId(id: string): string {
  if (id === '') return '—'
  if (id.length <= 16) return id
  return `${id.slice(0, 5)}…${id.slice(-7)}`
}

export function duration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export function count(n: number): string {
  return n.toLocaleString('en-US')
}
